import { routeIdForMarketingParam } from "@/lib/route-slugs";
import { DEFAULT_LOCALE, localePath, normalizeLocale } from "@/lib/i18n";

export const MAX_SIGNUP_DATES = 3;

type SearchLike = Pick<URLSearchParams, "get" | "getAll">;

export type SignupRouteCandidate = {
  name: string;
  param: string;
  groupName?: string | null;
};

function routeCodeFromCandidate(route: Pick<SignupRouteCandidate, "name">): string | null {
  const match = route.name.match(/\b(?:route|ruta|rota)\s*(\d)\s*[- ]\s*([a-d])\b/i);
  return match ? `${match[1]}${match[2]}`.toUpperCase() : null;
}

export function defaultSignupRouteSlugs(routes: readonly SignupRouteCandidate[]): string[] {
  const normalized = routes
    .map((route) => ({
      name: route.name.trim(),
      param: route.param.trim(),
      groupName: (route.groupName ?? "").trim(),
    }))
    .filter((route) => route.param);

  if (normalized.length === 0) return [];

  const recommended = ["1B", "2A", "3B"]
    .map((code) => normalized.find((route) => routeCodeFromCandidate(route) === code)?.param)
    .find((param): param is string => Boolean(param));
  if (recommended) return [recommended];

  const classic = normalized.find((route) => /\bclassic\b/i.test(`${route.name} ${route.groupName}`));
  if (classic) return [classic.param];

  const circuit2 = normalized.find((route) => /\bcircuit\s*2\b/i.test(route.groupName));
  if (circuit2) return [circuit2.param];

  return [normalized[0]!.param];
}

export function routeIdFromMarketingParam(raw: string | null | undefined): string | null {
  return routeIdForMarketingParam(raw);
}

export function routeIdsFromSetupParams(
  searchParams: SearchLike,
  availableRouteIds: readonly string[],
): string[] {
  const available = new Set(availableRouteIds.map((id) => String(id).trim()).filter(Boolean));
  const routeParams = [...searchParams.getAll("routeSlug"), ...searchParams.getAll("routeId")];
  const out: string[] = [];

  for (const raw of routeParams) {
    const id = routeIdFromMarketingParam(raw);
    if (!id || !available.has(id) || out.includes(id)) continue;
    out.push(id);
  }

  return out;
}

export function ymdFromSetupParam(raw: string | null | undefined): string | null {
  const value = (raw ?? "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split("-").map((part) => Number(part));
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return value;
}

export function dateStringsFromSetupParams(searchParams: SearchLike, max = MAX_SIGNUP_DATES): string[] {
  const rawDates = searchParams.getAll("date");
  const fallbackDates = [searchParams.get("dateStart"), searchParams.get("dateEnd")];
  const candidates = rawDates.length > 0 ? rawDates : fallbackDates;
  const out: string[] = [];

  for (const raw of candidates) {
    const date = ymdFromSetupParam(raw);
    if (!date || out.includes(date)) continue;
    out.push(date);
    if (out.length >= max) break;
  }

  return out;
}

export function partySizeFromSetupParams(searchParams: SearchLike): string | null {
  const value = (searchParams.get("partySize") ?? "").trim();
  if (!value) return null;
  const number = Number(value);
  if (!Number.isFinite(number) || number < 1) return null;
  return String(Math.trunc(number));
}

export function buildBuilderSetupPath(input: {
  routeSlugs: readonly string[];
  dates: readonly string[];
  partySize: string | number;
  locale?: string;
  attractionId?: string;
}): string {
  const params = new URLSearchParams();
  const attractionId = (input.attractionId ?? "").trim();
  if (attractionId) params.set("attraction", attractionId);
  for (const routeSlug of input.routeSlugs) {
    const value = routeSlug.trim();
    if (value) params.append("routeSlug", value);
  }
  for (const date of input.dates) {
    const value = date.trim();
    if (value) params.append("date", value);
  }
  const partySize = String(input.partySize).trim();
  if (partySize) params.set("partySize", partySize);
  const locale = (input.locale ?? "").trim();
  if (locale) params.set("locale", locale);
  const query = params.toString();
  return query ? `/app/builder?${query}` : "/app/builder";
}

export function buildLoginSetupPath(input: {
  routeSlugs: readonly string[];
  dates: readonly string[];
  partySize: string | number;
  locale?: string;
  attractionId?: string;
}): string {
  return `/login?mode=create&next=${encodeURIComponent(buildBuilderSetupPath(input))}`;
}

export function buildSetupContactPath(input: {
  routeLabels: readonly string[];
  dates: readonly string[];
  partySize: string | number;
  targetCount?: number;
  source?: string;
  locale?: string;
}): string {
  const params = new URLSearchParams();
  const routeLabels = input.routeLabels.map((label) => label.trim()).filter(Boolean);
  const dates = input.dates.map((date) => date.trim()).filter(Boolean);
  const partySize = String(input.partySize).trim();
  const targetCount = input.targetCount && input.targetCount > 0 ? Math.trunc(input.targetCount) : null;
  const source = (input.source ?? "Public setup form").trim();

  const locale = normalizeLocale(input.locale);
  const lines =
    locale === "es"
      ? [
        "Estoy considerando un monitor de MachuPing y quiero preguntar antes de iniciar sesión o pagar.",
        routeLabels.length ? `Rutas: ${routeLabels.join(", ")}` : "Rutas: aún no elegidas",
        dates.length ? `Fechas: ${dates.join(", ")}` : "Fechas: aún no elegidas",
        partySize ? `Tamaño del grupo: ${partySize}` : "Tamaño del grupo: aún no elegido",
        targetCount ? `Revisiones ruta/fecha: ${targetCount}` : null,
        source ? `Origen: ${source}` : null,
      ].filter((line): line is string => Boolean(line))
      : locale === "pt"
        ? [
        "Estou considerando um monitor da MachuPing e quero perguntar antes de entrar ou pagar.",
        routeLabels.length ? `Rotas: ${routeLabels.join(", ")}` : "Rotas: ainda não escolhidas",
        dates.length ? `Datas: ${dates.join(", ")}` : "Datas: ainda não escolhidas",
        partySize ? `Tamanho do grupo: ${partySize}` : "Tamanho do grupo: ainda não escolhido",
        targetCount ? `Verificações rota/data: ${targetCount}` : null,
        source ? `Origem: ${source}` : null,
      ].filter((line): line is string => Boolean(line))
        : [
        "I am considering a MachuPing monitor and want to ask before signing in or paying.",
        routeLabels.length ? `Routes: ${routeLabels.join(", ")}` : "Routes: not chosen yet",
        dates.length ? `Dates: ${dates.join(", ")}` : "Dates: not chosen yet",
        partySize ? `Party size: ${partySize}` : "Party size: not chosen yet",
        targetCount ? `Route/date checks: ${targetCount}` : null,
        source ? `Source: ${source}` : null,
      ].filter((line): line is string => Boolean(line));

  params.set("topic", "setup_question");
  if (locale !== DEFAULT_LOCALE) params.set("locale", locale);
  params.set("message", lines.join("\n"));
  return `${localePath(locale, "/contact")}?${params.toString()}`;
}
